import apiClient from '../client';
import { useAuthStore } from '../../store/authStore';
import type { User } from '../../types';
import type { Account } from '../../store/authStore';

interface LoginRequest {
  email: string;
  password: string;
}

interface LoginResponse {
  user: User;
  accounts: Account[];
  token?: string;
}

interface SelectAccountRequest {
  userId: string;
  accountId: string;
}

interface SelectAccountResponse {
  token: string;
  firebaseToken?: string;
  user: User;
  account: Account;
}

interface SwitchAccountRequest {
  accountId: string;
}

interface SwitchAccountResponse {
  token: string;
  firebaseToken?: string;
  account: Account;
}

interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  phone?: string;
  role: 'AO' | 'BC' | 'Driver' | 'Maintainer';
  companyId?: string;
}

interface RegisterResponse {
  token: string;
  user: User;
}

class AuthService {
  private static BASE_PATH = '/api/auth';

  /**
   * Login user
   */
  static async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      // Remove whitespace from credentials
      const processedData = {
        email: credentials.email.replace(/\s/g, ''),
        password: credentials.password.replace(/\s/g, ''),
      };

      const response = await apiClient.post<any, LoginResponse>(
        `${this.BASE_PATH}/login`,
        processedData
      );

      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  }

  /**
   * Register new user
   */
  static async register(data: RegisterRequest): Promise<RegisterResponse> {
    try {
      const processedData = {
        ...data,
        email: data.email.replace(/\s/g, ''),
        password: data.password.replace(/\s/g, ''),
      };

      const response = await apiClient.post<any, RegisterResponse>(
        `${this.BASE_PATH}/register`,
        processedData
      );

      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  }

  /**
   * Select account after login
   */
  static async selectAccount(data: SelectAccountRequest): Promise<SelectAccountResponse> {
    try {
      const response = await apiClient.post<any, SelectAccountResponse>(
        `${this.BASE_PATH}/select-account`,
        data
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Account selection failed');
    }
  }

  /**
   * Switch to a different account
   */
  static async switchAccount(data: SwitchAccountRequest): Promise<SwitchAccountResponse> {
    try {
      const response = await apiClient.post<any, SwitchAccountResponse>(
        `${this.BASE_PATH}/switch-account`,
        data
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Account switch failed');
    }
  }

  /**
   * Logout user
   */
  static async logout(): Promise<void> {
    try {
      await apiClient.post(`${this.BASE_PATH}/logout`);
    } catch (error) {
      // Ignore logout errors
      console.error('Logout error:', error);
    } finally {
      // Both copies, not just the bare key — leaving `auth-storage` saying
      // `isAuthenticated: true` is what made an expired session loop.
      useAuthStore.getState().clearAuth();
    }
  }

  /**
   * Get current user profile
   */
  static async getCurrentUser(): Promise<User> {
    try {
      const response = await apiClient.get<any, { user: User }>(
        `${this.BASE_PATH}/me`
      );
      return response.user;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch user');
    }
  }

  /**
   * Refresh token
   */
  static async refreshToken(): Promise<{ token: string }> {
    try {
      const response = await apiClient.post<any, { token: string }>(
        `${this.BASE_PATH}/refresh`
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Token refresh failed');
    }
  }
}

export default AuthService;
